import React, { useEffect, useState } from 'react';

import {
  Breakpoints,
  TextAppearance,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextWeight,
  useIsMobile,
} from '@vw-marketing/us-components';

import { ImageContainer } from './ImageContainer';
import {
  AdditionaUser,
  ImageState,
  Plan,
  Vehicles,
} from '../../../typings/main';
import { checkImageLoads } from '../../../../shared/utils/general';
import {
  StyledAdditionalUsersContainer,
  StyledHeaderWrapper,
  StyledSubscriptionsContainer,
  StyledVehicleCardWrapper,
  StyledVehicleDataWrapper,
} from './styled';
import { VehicleInformationContainer } from './VehicleInformationContainer';

interface CompleteVehicleCardProps {
  id: string;
  vehicle: Vehicles;
  fromRemoveVehicle?: boolean;
  translationHandler: (key: string) => string;
}

export const CompleteVehicleCard = ({
  id,
  vehicle,
  fromRemoveVehicle = false,
  translationHandler,
}: CompleteVehicleCardProps) => {
  const [imageState, setImageState] = useState<ImageState>({
    isValidUrl: false,
    imgUrl: '',
  });

  const isTablet: boolean = !useIsMobile(Breakpoints.b560);

  const representativeImgURLComplete = vehicle?.representativeImgURLComplete ?? '';
  const representativeImgURLPartial = vehicle?.representativeImgURLPartial ?? '';

  // Check URL validity
  useEffect(() => {
    const validateImages = async () => {
      if (await checkImageLoads(representativeImgURLComplete)) {
        setImageState({
          isValidUrl: true,
          imgUrl: representativeImgURLComplete,
        });
        return;
      }

      if (await checkImageLoads(representativeImgURLPartial)) {
        setImageState({
          isValidUrl: true,
          imgUrl: representativeImgURLPartial,
        });
        return;
      }

      setImageState({
        isValidUrl: false,
        imgUrl: '',
      });
    };

    validateImages();
  }, [representativeImgURLComplete, representativeImgURLPartial]);

  if (!vehicle) return <></>;

  const additionalUsers: AdditionaUser[] = vehicle.associatedUsers.filter(
    (user: AdditionaUser) => user.roles[0] !== 'PU',
  );

  const { modelName, modelYear, userRole, vin } = vehicle;

  return (
    <StyledVehicleCardWrapper
      hasImage={imageState.isValidUrl}
      key={id}
      className="complete-vehicle-card"
    >
      <StyledHeaderWrapper hasImage={imageState.isValidUrl}>
        {[
          isTablet && (
            <VehicleInformationContainer
              id={`vehicle-${id}-info`}
              modelName={modelName}
              modelYear={modelYear}
              userRole={userRole}
              vin={vin}
              {...{ fromRemoveVehicle }}
            />
          ),
          imageState.isValidUrl && (
            <ImageContainer
              imgSource={imageState.imgUrl}
              key={`vehicle-${id}-image`}
            />
          ),
          !isTablet && (
            <VehicleInformationContainer
              id={`vehicle-${id}-info`}
              modelName={modelName}
              modelYear={modelYear}
              userRole={userRole}
              vin={vin}
              {...{ fromRemoveVehicle }}
            />
          ),
        ].filter(Boolean)}
      </StyledHeaderWrapper>
      <StyledVehicleDataWrapper hasImage={imageState.isValidUrl}>
        {vehicle.internalSubscriptions.length > 0 && (
          <StyledSubscriptionsContainer>
            <TextCustom
              appearance={TextAppearance.copy200}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize16,
                  fontWeight: TextWeight.regular,
                  color: TextColor.primary,
                  lineHeight: TextLineHeight.TextLineHeight24,
                },
                [Breakpoints.b1920]: {
                  fontSize: TextSize.TextSize20,
                  fontWeight: TextWeight.regular,
                  color: TextColor.primary,
                  lineHeight: TextLineHeight.TextLineHeight32,
                },
              }}
            >
              {translationHandler(
                'accountSettings.deactivate.myvwaccount.vw.subscriptions.label',
              )}
            </TextCustom>
            {vehicle.internalSubscriptions.map((plan: Plan) => (
              <TextCustom
                key={plan.package.sku}
                appearance={TextAppearance.copy100}
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize14,
                    fontWeight: TextWeight.regular,
                    color: TextColor.secondary,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                  [Breakpoints.b1920]: {
                    fontSize: TextSize.TextSize16,
                    fontWeight: TextWeight.regular,
                    color: TextColor.secondary,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                }}
              >
                {plan.package.name}
              </TextCustom>
            ))}
          </StyledSubscriptionsContainer>
        )}
        {additionalUsers.length > 0 && (
          <StyledAdditionalUsersContainer>
            <TextCustom
              appearance={TextAppearance.copy200}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize16,
                  fontWeight: TextWeight.regular,
                  color: TextColor.primary,
                  lineHeight: TextLineHeight.TextLineHeight24,
                },
                [Breakpoints.b1920]: {
                  fontSize: TextSize.TextSize20,
                  fontWeight: TextWeight.bold,
                  color: TextColor.primary,
                  lineHeight: TextLineHeight.TextLineHeight32,
                },
              }}
            >
              {translationHandler(
                'accountSettings.deactivate.myvwaccount.additionalUsers.label',
              )}
            </TextCustom>
            {additionalUsers.map((user: AdditionaUser) => (
              <TextCustom
                appearance={TextAppearance.copy100}
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize14,
                    fontWeight: TextWeight.regular,
                    color: TextColor.secondary,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                  [Breakpoints.b1920]: {
                    fontSize: TextSize.TextSize16,
                    fontWeight: TextWeight.regular,
                    color: TextColor.secondary,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                }}
              >
                {user.firstName || ''} {user.lastName || ''}
              </TextCustom>
            ))}
          </StyledAdditionalUsersContainer>
        )}
      </StyledVehicleDataWrapper>
    </StyledVehicleCardWrapper>
  );
};
